import type { AxiosInstance } from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { UnmatchedReasonRow } from "@ecomally/contracts/reports/unmatched-reasons";

import { apiErrorMessage } from "../lib/api.js";
import { JOURNEY_UNIDENTIFIED, type JourneyLevel, type JourneyTarget } from "../lib/customer-journey.js";
import { rangeProblem, type DateRange } from "../lib/dates.js";
import {
  coverage,
  fetchReport,
  fetchUnmatchedReasons,
  supportsUnmatchedReasons,
  visibleColumnsFor,
  ALLOWED_GOOGLE_HIERARCHY_BY_CAMPAIGN_TYPE,
  CAMPAIGN_STATUSES,
  DEFAULT_GOOGLE_HIERARCHIES,
  GOOGLE_CAMPAIGN_TYPES,
  GOOGLE_CAMPAIGN_TYPE_LABELS,
  GOOGLE_CAMPAIGN_HIERARCHY_LABELS,
  GOOGLE_HIERARCHY_CHILD,
  META_CAMPAIGN_HIERARCHIES,
  META_CAMPAIGN_HIERARCHY_LABELS,
  GOOGLE_UNATTRIBUTED_LABELS,
  META_UNATTRIBUTED_LABELS,
  PAGE_SIZES,
  type CampaignStatus,
  type GoogleCampaignHierarchy,
  type GoogleCampaignType,
  type MetaCampaignHierarchy,
  type ReportColumn,
  type ReportDefinition,
  type ReportRow,
  type ReturnsView,
  type StoreTotalSales,
  type Unattributed,
} from "../lib/reports.js";
import { DataAsOfNote } from "../components/DataAsOfNote.js";
import { ReturnsBridge } from "../components/ReturnsBridge.js";
import { StoreTotalSalesCards } from "../components/StoreTotalSalesCards.js";
import { BarChart3, ChevronRight, Lock, LockOpen, Search } from "lucide-react";

import { ReportGuideAccordion } from "../components/ReportGuide/ReportGuideAccordion.js";
import { ReportGuideToggleButton } from "../components/ReportGuide/ReportGuideToggleButton.js";
import { reportGuideContent } from "./reportGuides.js";

import { CustomerJourneyModal } from "../components/CustomerJourneyModal.js";
import { DateRangePicker } from "../components/DateRangePicker.js";
import { ExportButton } from "../components/ExportButton.js";
import { ReportTable } from "../components/ReportTable.js";
import { UnmatchedReasonsDrawer } from "../components/UnmatchedReasonsDrawer.js";
import { Button } from "../components/ui/button.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { Select } from "../components/ui/select.js";
import { AttributionModelPicker } from "../components/AttributionModelPicker.js";
import { AttributionBreakdownTable, type BreakdownFilters } from "../components/AttributionBreakdownTable.js";

/**
 * A report: pick a window, read the numbers.
 *
 * WHY THERE IS ONE OF THESE AND NOT FIVE. Legacy had a file per report, each with its own
 * copy of the date state, the axios call, the loading flag and the table markup —
 * 600-1,500 lines apiece. Four of the five had drifted from each other by the time they
 * were read for this port. Everything that genuinely differs between the reports lives in
 * `lib/reports.ts`; what is left is this, once.
 *
 * THE STORE'S TIMEZONE IS SHOWN, NOT ASSUMED. The date inputs are the browser's calendar;
 * the API counts days in the STORE's. Those disagree for most of the day for a merchant in
 * Asia/Kolkata working with a laptop set to UTC. The response echoes the zone it used and
 * this screen prints it, so a merchant comparing against Shopify's own reports can see
 * which boundary produced the difference instead of assuming one of the two is broken.
 */
/** Totals arrive as 64-bit counts, so they can be a string. Anything unusable is "unknown". */
/**
 * What the search box matches, said in its placeholder — the API's closed column list
 * (REPORT_SEARCH_COLUMNS in packages/ch): a landing page by URL, a source by name, a campaign
 * (ad set, ad …) by name or id.
 */
function searchHint(report: ReportDefinition): string {
  if (report.metaHierarchy === true || report.googleHierarchy === true) return "by name or id";
  if (report.key === "source-attribution") return "sources";
  // A page also matches by the links its visits arrived on — "pinned", a utm_content (2026-10-28).
  return "pages or link text";
}

/**
 * "Lock platform columns", remembered per browser — a viewing preference, not a setting anyone
 * else needs. Storage can be unavailable (a private window, blocked site data): it then reads off.
 */
const LOCK_PLATFORM_COLUMNS_KEY = "ecomally.reports.lockPlatformColumns";

function readLockPlatformColumns(): boolean {
  try {
    return window.localStorage.getItem(LOCK_PLATFORM_COLUMNS_KEY) === "1";
  } catch (error) {
    void error;
    return false;
  }
}

function writeLockPlatformColumns(locked: boolean): void {
  try {
    window.localStorage.setItem(LOCK_PLATFORM_COLUMNS_KEY, locked ? "1" : "0");
  } catch (error) {
    void error;
  }
}

function parseTotal(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * A hierarchy level's display label, for whichever platform's report is showing. The two
 * platforms use different vocabularies for the same idea ("adgroup" vs "adset"), so the
 * lookup has to know which label set applies rather than sharing one map.
 */
function hierarchyLabel(report: ReportDefinition, level: string): string {
  if (report.googleHierarchy === true) {
    return (
      GOOGLE_CAMPAIGN_HIERARCHY_LABELS[
        level as keyof typeof GOOGLE_CAMPAIGN_HIERARCHY_LABELS
      ] ?? level
    );
  }
  if (report.metaHierarchy === true) {
    return (
      META_CAMPAIGN_HIERARCHY_LABELS[
        level as keyof typeof META_CAMPAIGN_HIERARCHY_LABELS
      ] ?? level
    );
  }
  return level;
}

/**
 * What clicking a row at this Meta level drills into. `null` means "ad" — the bottom of
 * the chain, nothing further to click into.
 *
 * Kept local to this screen rather than added to `lib/reports.ts`: it is purely a UI
 * question ("what does a click do"), not part of the report contract the API and the
 * client both need to agree on the way `META_CAMPAIGN_HIERARCHIES` is.
 */
const META_HIERARCHY_CHILD: Record<
  MetaCampaignHierarchy,
  MetaCampaignHierarchy | null
> = {
  campaign: "adset",
  adset: "ad",
  ad: null,
};

/** One step of a Meta drill-down trail: the entity that was clicked into, and its level. */
interface MetaDrillCrumb {
  readonly id: string;
  readonly name: string;
  readonly level: MetaCampaignHierarchy;
}

/**
 * One step of a Google drill-down trail — same idea as Meta's, but the level a click
 * lands on depends on the selected campaign type (see `GOOGLE_HIERARCHY_CHILD`):
 * "campaign" -> "assetgroup" for Performance Max, "campaign" -> "adgroup" -> "ad" for
 * Search/Demand Gen/Video. Shopping, Multi-channel, "all types" and Unattributed never
 * produce one of these — clicking a row does nothing at those types.
 */
interface GoogleDrillCrumb {
  readonly id: string;
  readonly name: string;
  readonly level: GoogleCampaignHierarchy;
}

export function ReportScreen({
  client,
  report,
  tenantId,
  range,
  onRangeChange,
  storeName,
}: {
  readonly client: AxiosInstance;
  readonly report: ReportDefinition;
  /** Sent as `X-Tenant-Id` by the client itself; here it is the reason to REFETCH. */
  readonly tenantId: string;
  /** Owned by the shell, so it survives switching reports. */
  readonly range: DateRange;
  readonly onRangeChange: (range: DateRange) => void;
  /** Named in the empty state, so "no data" says which store had none. */
  readonly storeName: string;
}): React.JSX.Element {
  const [limit, setLimit] = useState<number>(50);
  const [offset, setOffset] = useState(0);
  /**
   * CONSOLIDATED (the default — the report exactly as it always was), or the same rows broken down by DAY or by WEEK of the range:
   * each source / campaign a "Grand Total" line with its days under it, loaded as the merchant scrolls (AttributionBreakdownTable).
   * Only the three attribution reports have it (`report.periodBreakdown`).
   */
  const [view, setView] = useState<"consolidated" | "day" | "week">("consolidated");
  // The report guide accordion — closed by default; the toggle button's own tooltip is
  // what draws first-time attention to it (see ReportGuideToggleButton).
  const [showGuideSection, setShowGuideSection] = useState(false);
  const [sortBy, setSortBy] = useState<string | null>(report.defaultSort);
  // `all` — see CAMPAIGN_STATUSES. A filter that hides rows before anyone touches it
  // makes the page silently disagree with Source Attribution.
  const [status, setStatus] = useState<CampaignStatus>("all");
  // Google-only: restricts to one Ads channel type, or the synthetic "UNATTRIBUTED"
  // bucket. "all" mirrors the API leaving campaignType unset, and is the report's own
  // opening state too (owner's decision, 2026-09-28 — previously opened pre-filtered to
  // Performance Max). Meaningless (and unused) on every other report, including Meta's —
  // Meta has no campaignType concept.
  const [campaignType, setCampaignType] = useState<GoogleCampaignType | "all">("all");
  // Drill level. Shared state for both platforms' campaign reports — its valid values
  // depend on report.googleHierarchy vs report.metaHierarchy, both of which default to
  // "campaign" server-side. Kept as `string` rather than a union of the two hierarchy
  // types since only one is ever relevant per report.
  const [hierarchy, setHierarchy] = useState<string>("campaign");

  /**
   * The reconciliation line's name, for the level currently on screen. Both campaign
   * reports scope that row to the drill level server-side, so the label has to follow it;
   * every other report keeps its own fixed wording. The two platforms need separate maps
   * because they name the middle rung differently — Meta "ad set", Google "ad group".
   */
  const unattributedLabel =
    report.metaHierarchy === true
      ? (META_UNATTRIBUTED_LABELS[
          hierarchy as keyof typeof META_UNATTRIBUTED_LABELS
        ] ?? report.unattributedLabel)
      : report.googleHierarchy === true
        ? (GOOGLE_UNATTRIBUTED_LABELS[
            hierarchy as keyof typeof GOOGLE_UNATTRIBUTED_LABELS
          ] ?? report.unattributedLabel)
        : report.unattributedLabel;
  /**
   * The unmatched-reasons drawer. `null` = closed; the rows arrive after it opens.
   *
   * Fetched on open rather than with the report, because most page views never open it
   * and the report already issues three concurrent ClickHouse queries.
   */
  const [reasonsOpen, setReasonsOpen] = useState(false);
  const [reasons, setReasons] = useState<readonly UnmatchedReasonRow[] | null>(null);
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [reasonsError, setReasonsError] = useState<string | null>(null);

  const [direction, setDirection] = useState<"asc" | "desc">("desc");
  // Source Attribution's model picker only — see AttributionModelToggle. `null` objective
  // means the free multi-select is in charge; picking one PINS `selectedModels` to it and
  // the picker below sends `visibleColumnsFor` the objective's own model keys instead.
  const [selectedObjective, setSelectedObjective] = useState<string | null>(
    null,
  );
  const [selectedModels, setSelectedModels] = useState<ReadonlySet<string>>(
    () => new Set(report.attributionModelToggle?.defaultModelKeys ?? []),
  );
  // Source Attribution's last-click figures before or after cancellations and refunds — before by
  // default (owner, 2026-10-30): what the channels sold. See ReturnsView.
  const [returnsView, setReturnsView] = useState<ReturnsView>("original");

  /**
   * Meta-only drill-down trail, built by clicking a row rather than the Level dropdown.
   * `[]` = top-level campaigns. `[campaign]` = inside one campaign's adsets.
   * `[campaign, adset]` = inside one adset's ads. Never touched for Google: its hierarchy
   * branches per campaignType (Shopping vs PMax vs Search all mean something different at
   * "adgroup"), so it keeps the explicit Type + Level controls rather than a linear click
   * chain. Reset alongside `hierarchy`/`campaignType` whenever the report or store change.
   */
  const [metaDrillPath, setMetaDrillPath] = useState<readonly MetaDrillCrumb[]>(
    [],
  );

  const metaCampaignId =
    report.metaHierarchy === true ? metaDrillPath[0]?.id : undefined;
  const metaAdsetId =
    report.metaHierarchy === true ? metaDrillPath[1]?.id : undefined;

  /**
   * Google-only drill-down trail, built by clicking a row. Length varies by campaign
   * type: two steps for Performance Max (campaign -> assetgroup), three for
   * Search/Demand Gen/Video (campaign -> adgroup -> ad). Shopping and Multi-channel never
   * populate this — see `GOOGLE_HIERARCHY_CHILD`. Reset alongside `hierarchy`/
   * `campaignType` whenever the report, store, or campaign type change.
   */
  const [googleDrillPath, setGoogleDrillPath] = useState<
    readonly GoogleDrillCrumb[]
  >([]);

  const googleCampaignId =
    report.googleHierarchy === true ? googleDrillPath[0]?.id : undefined;
  const googleAdgroupId =
    report.googleHierarchy === true ? googleDrillPath[1]?.id : undefined;

  const [rows, setRows] = useState<readonly ReportRow[]>([]);
  /**
   * THE SEARCH BOX SEARCHES THE WHOLE REPORT (2026-10-16). It filtered the rows already
   * fetched, so a match on page two was invisible on page one — dev, 5 Oct: "summer" found
   * 2 of 5 campaigns at 50 rows a page, all 5 at 500. `query` is what is typed; `search` is
   * what was asked, trimmed, and is sent to the API, which filters every row in SQL and pages
   * the matches.
   *
   * SENT ON ENTER, NOT AS YOU TYPE (owner, 2026-10-16). A search reruns the whole report — uat,
   * Zeraki's Google report, ~37 s and 68M rows read whether searched or not — and a request the
   * screen has moved past keeps running in ClickHouse. A debounce fired one of those at every
   * pause in typing (five in two minutes on the first day). Clearing the box is the exception:
   * it asks for nothing new, so the full report comes back at once.
   */
  const [lockPlatformColumns, setLockPlatformColumns] = useState(readLockPlatformColumns);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const submitSearch = (text: string): void => {
    const next = text.trim();
    if (next === search) return;
    // A new search starts at its first page: offset 300 of the old answer means nothing.
    setOffset(0);
    setSearch(next);
  };
  const searchPending = query.trim() !== search;
  const [timezone, setTimezone] = useState<string | null>(null);
  const [dataAsOf, setDataAsOf] = useState<string | null>(null);
  const [unattributed, setUnattributed] = useState<Unattributed | null>(null);
  const [totals, setTotals] = useState<Readonly<Record<string, number>> | null>(
    null,
  );
  const [storeTotalSales, setStoreTotalSales] = useState<StoreTotalSales | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * WHICH RESPONSE IS ALLOWED TO WIN. Changing the sort twice quickly starts two requests,
   * and they can finish in either order — so the SECOND click could be overwritten by the
   * FIRST response and leave the table showing data that contradicts its own headers.
   * Each request takes a ticket; only the newest ticket may write state.
   */
  const ticket = useRef(0);

  const problem = useMemo(() => rangeProblem(range), [range]);

  /**
   * THE CUSTOMER JOURNEY MODAL — a Purchases cell opens the orders behind its number
   * (`ecom_code`'s `CustomerJourneyModal`). This turns "which cell, in which row, under
   * which drill-down" into the request the API wants.
   */
  const [journeyTarget, setJourneyTarget] = useState<JourneyTarget | null>(null);
  const closeJourney = useCallback(() => {
    setJourneyTarget(null);
  }, []);

  const isCampaignReport =
    report.metaHierarchy === true || report.googleHierarchy === true;

  /**
   * Which rows have Purchases cells that can open a list.
   *
   * NEVER WHILE A REFETCH IS IN FLIGHT: `hierarchy` changes the instant the Level
   * dropdown does, but the rows on screen are still the previous level's until the response
   * lands. A click in that gap would send the OLD row's id labelled as the NEW level — an
   * ad id filtered as if it were a campaign — and come back empty for a cell that plainly
   * shows purchases. (Legacy hit the same race and worked around it by stamping a
   * `_hierarchy` onto each row.)
   */
  const journeyRowEligible = (row: ReportRow): boolean => {
    if (loading) return false;
    if (!isCampaignReport) {
      const source = String(row["ea_source"] ?? "");
      // `[Blank]` has no source to filter by; `pending` is Source Attribution's own
      // "not scored by DDA yet" bucket — a bookkeeping row, not a source.
      return source !== "" && source !== "[Blank]" && source !== "pending";
    }
    return (
      campaignType === "UNATTRIBUTED" || String(row["campaign_id"] ?? "") !== ""
    );
  };

  const openJourney = (row: ReportRow | null, column: ReportColumn): void => {
    const model = column.journeyModel;
    if (model === undefined) return;

    // Source Attribution: the row IS the source.
    if (!isCampaignReport) {
      const source = String(row?.["ea_source"] ?? "");
      if (source === "") return;
      setJourneyTarget({ model, source, label: source });
      return;
    }

    const source = report.metaHierarchy === true ? "meta" : "google";
    // Google's MULTI_CHANNEL "Asset group" is really ad-group grain — its id lives in
    // google_ad_group_id, the same column "adgroup" reads (see googleHierarchyShape).
    const level = (
      report.googleHierarchy === true &&
      hierarchy === "assetgroup" &&
      campaignType === "MULTI_CHANNEL"
        ? "adgroup"
        : hierarchy
    ) as JourneyLevel;

    // The "not matched" line, and Google's UNATTRIBUTED type (which is that same bucket
    // shown as a row): the touches of this platform with NO id at this level.
    const unmatched = row === null || campaignType === "UNATTRIBUTED";
    const entityId = unmatched
      ? JOURNEY_UNIDENTIFIED
      : String(row["campaign_id"] ?? "");
    const label = unmatched
      ? (unattributedLabel ?? "Not matched to a campaign")
      : String(row["campaign_name"] ?? "") || entityId;

    // The drill-down the table was under, so the list is scoped exactly as the number was.
    const parentCampaignId =
      report.metaHierarchy === true ? metaCampaignId : googleCampaignId;
    const parentAdsetId =
      report.metaHierarchy === true ? metaAdsetId : googleAdgroupId;

    setJourneyTarget({
      model,
      source,
      label,
      level,
      entityId,
      ...(parentCampaignId === undefined ? {} : { parentCampaignId }),
      ...(parentAdsetId === undefined ? {} : { parentAdsetId }),
    });
  };

  /**
   * Hierarchy levels valid right now, for whichever platform's report is showing.
   *
   * Google's set depends on the selected campaignType (mirrors
   * ALLOWED_GOOGLE_HIERARCHY_BY_CAMPAIGN_TYPE / DEFAULT_GOOGLE_HIERARCHIES). Meta's does
   * not — every level is always offered, since Meta has no type-specific tables the way
   * Google's assetgroup/productgroup levels do.
   */
  const allowedHierarchies: readonly string[] = useMemo(() => {
    if (report.googleHierarchy === true) {
      return campaignType === "all"
        ? DEFAULT_GOOGLE_HIERARCHIES
        : ALLOWED_GOOGLE_HIERARCHY_BY_CAMPAIGN_TYPE[campaignType];
    }
    if (report.metaHierarchy === true) {
      return META_CAMPAIGN_HIERARCHIES;
    }
    return [];
  }, [report, campaignType]);

  /**
   * "All types" has no single hierarchy that means the same thing across every Google
   * type, and UNATTRIBUTED and OTHER only ever have one valid level — "campaign" — so the Level
   * control is locked rather than left clickable with nothing meaningful to choose.
   * Meta has no such case: it has no campaignType at all, so its Level control is never
   * locked.
   */
  const hierarchyLocked =
    report.googleHierarchy === true &&
    (campaignType === "all" || campaignType === "UNATTRIBUTED" || campaignType === "OTHER");

  // Force the level back to "campaign" whenever it becomes locked — covers both the type
  // select's onChange and the reset-on-report-switch effect from one place, so the two
  // can never drift out of sync. The click-trail is meaningless once locked too: there is
  // no lower level to have drilled into.
  useEffect(() => {
    if (hierarchyLocked) {
      setHierarchy("campaign");
      setGoogleDrillPath([]);
    }
  }, [hierarchyLocked]);

  /**
   * EVERYTHING THAT DECIDES WHICH ROWS EXIST — search, sort, status, drill level and parent — apart from the paging. The
   * consolidated table pages through it; the Day / Week view asks for the same rows four at a time.
   */
  const reportFilters = useMemo<BreakdownFilters>(
    () => ({
      ...(search !== "" ? { search } : {}),
      ...(report.sortable && sortBy !== null ? { sortBy, direction } : {}),
      ...(report.statusFilter === true ? { status } : {}),
      ...(report.googleHierarchy === true || report.metaHierarchy === true
        ? {
            hierarchy,
            ...(report.googleHierarchy === true && campaignType !== "all" ? { campaignType } : {}),
            // Meta only: scope to whichever campaign/adset the merchant clicked into.
            ...(report.metaHierarchy === true
              ? {
                  ...(metaCampaignId !== undefined ? { campaignId: metaCampaignId } : {}),
                  ...(metaAdsetId !== undefined ? { adsetId: metaAdsetId } : {}),
                }
              : {}),
            // Google only: scope to whichever campaign/ad group the merchant clicked into. Unset for Shopping, Multi-channel,
            // "all types" and Unattributed — GOOGLE_HIERARCHY_CHILD never produces a trail for those.
            ...(report.googleHierarchy === true
              ? {
                  ...(googleCampaignId !== undefined ? { campaignId: googleCampaignId } : {}),
                  ...(googleAdgroupId !== undefined ? { adgroupId: googleAdgroupId } : {}),
                }
              : {}),
          }
        : {}),
    }),
    [report, search, sortBy, direction, status, campaignType, hierarchy, metaCampaignId, metaAdsetId, googleCampaignId, googleAdgroupId],
  );

  const breakdownMode = view !== "consolidated" && report.periodBreakdown !== undefined;

  const load = useCallback(async () => {
    // The Day / Week view fetches its own rows; the consolidated page is not needed (nor paid for) while it is open.
    if (problem !== null || breakdownMode) return;

    const mine = ++ticket.current;
    setLoading(true);
    setError(null);

    try {
      const payload = await fetchReport(client, report, {
        from: range.from,
        to: range.to,
        limit,
        offset,
        ...reportFilters,
      });
      if (mine !== ticket.current) return;
      setRows(payload.rows);
      setTimezone(payload.timezone);
      setUnattributed(payload.unattributed ?? null);
      setTotals(payload.totals ?? null);
      setStoreTotalSales(payload.storeTotalSales ?? null);
      setDataAsOf(payload.dataAsOf ?? null);
    } catch (caught) {
      if (mine !== ticket.current) return;
      /**
       * A 402 has ALREADY been turned into the billing banner by the api client, and it
       * is re-thrown so callers stop. Showing "Subscription required" a second time here,
       * in red, next to an empty table would read as a failure rather than as an offer.
       * The rows are cleared either way: stale numbers under a new date range are worse
       * than none.
       */
      setRows([]);
      setUnattributed(null);
      setTotals(null);
      setStoreTotalSales(null);
      setDataAsOf(null);
      setError(apiErrorMessage(caught, "Could not load this report."));
    } finally {
      if (mine === ticket.current) setLoading(false);
    }
  }, [client, report, range, limit, offset, reportFilters, problem, breakdownMode]);

  useEffect(() => {
    void load();
  }, [load, tenantId]);

  /**
   * The drawer's data, refetched whenever the window it describes changes.
   *
   * Keyed on the date range rather than fetched once on open, so moving the date picker
   * with the drawer showing updates it instead of leaving last week's reasons beside this
   * week's table — the kind of stale panel that gets screenshotted and sent to an agency.
   *
   * `cancelled` drops a response whose request has been superseded; two range changes in
   * quick succession can otherwise land out of order and show the older answer.
   */
  useEffect(() => {
    if (!reasonsOpen) return undefined;
    let cancelled = false;
    setReasonsLoading(true);
    setReasonsError(null);
    void fetchUnmatchedReasons(client, report, { from: range.from, to: range.to })
      .then((rows) => {
        if (!cancelled) setReasons(rows);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setReasonsError(apiErrorMessage(caught, "Could not load the breakdown."));
      })
      .finally(() => {
        if (!cancelled) setReasonsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reasonsOpen, client, report, range.from, range.to]);

  /**
   * Drilling into a campaign closes the drawer.
   *
   * The breakdown is campaign level only — one level down the unmatched line means "no ad
   * set", whose reasons are a different list. Leaving the panel open would caption the
   * new table with the old question.
   */
  useEffect(() => {
    if (!supportsUnmatchedReasons(report, hierarchy)) setReasonsOpen(false);
  }, [report, hierarchy]);

  // A new report or a new store resets the page — offset 12 of a report you were not
  // looking at is never what you meant, and can be an empty page for no visible reason.
  useEffect(() => {
    setOffset(0);
  }, [range]);

  useEffect(() => {
    setOffset(0);
    setSortBy(report.defaultSort);
    setDirection("desc");
    // Reset with the report: an "active only" filter carried onto a different report —
    // or a different STORE, whose campaigns are entirely different — is a hidden reason
    // for missing rows.
    setStatus("all");
    // Same reasoning: a type/asset-group drill carried onto Meta's report, or onto a
    // different store's report, is a hidden reason for missing rows. Every report,
    // Google included, resets to campaignType "all" and hierarchy "campaign".
    setCampaignType("all");
    setHierarchy("campaign");
    // A click-drill trail from Meta's report carried onto a different report — or the
    // same report on a different store, whose campaign ids mean nothing here — is a
    // hidden reason a click-scoped fetch comes back empty. Google's trail is reset for
    // the same reason.
    setMetaDrillPath([]);
    setGoogleDrillPath([]);
    // An open journey list describes ONE row of the report and store it was opened from.
    setJourneyTarget(null);
    // Same reasoning again: an objective or model set from Source Attribution has no
    // meaning on any other report, and carrying it across a store switch would silently
    // change which columns render with no control on screen explaining why.
    setSelectedObjective(null);
    setSelectedModels(
      new Set(report.attributionModelToggle?.defaultModelKeys ?? []),
    );
    // A Day view of Source Attribution means nothing on another report, and the default is the report as it always was.
    setView("consolidated");
  }, [report, tenantId]);

  const attributionToggle = report.attributionModelToggle;

  /** A week view of a range of seven days or fewer is the day view; and the UNATTRIBUTED type is one bucket, not a series. */
  const rangeDays = useMemo(() => {
    const spanMs = new Date(`${range.to}T00:00:00Z`).getTime() - new Date(`${range.from}T00:00:00Z`).getTime();
    return Math.round(spanMs / 86_400_000) + 1;
  }, [range.from, range.to]);
  const weekDisabled = rangeDays <= 7;
  const periodViewDisabled = report.googleHierarchy === true && campaignType === "UNATTRIBUTED";
  useEffect(() => {
    if (view === "week" && weekDisabled) setView("day");
    if (view !== "consolidated" && periodViewDisabled) setView("consolidated");
  }, [view, weekDisabled, periodViewDisabled]);

  const activeModelKeys = useMemo(() => {
    if (attributionToggle === undefined) return selectedModels;
    const objective = attributionToggle.objectives.find(
      (candidate) => candidate.key === selectedObjective,
    );
    return objective === undefined
      ? selectedModels
      : new Set(objective.modelKeys);
  }, [attributionToggle, selectedObjective, selectedModels]);

  const visibleColumns = useMemo(
    () => visibleColumnsFor(report, activeModelKeys, returnsView),
    [report, activeModelKeys, returnsView],
  );

  // The API already returned only the matches (see `search`); nothing is filtered here.
  const visibleRows = rows;

  const canPageForward = rows.length === limit;

  /**
   * "Total" only when these rows ARE the whole answer. With more pages behind the one on
   * screen, or a search narrowing it, the footer sums what is visible — and must say so,
   * or it reads as the total for the range and is wrong by however much is off-screen.
   */
  /**
   * COVERAGE, stated before anyone compares two pages and concludes the data is broken.
   * See `coverage()` — this is the sentence that explains a 2,691-order difference.
   */
  const tracked = useMemo(
    () =>
      report.coverageKey === undefined
        ? null
        : coverage(rows, unattributed, report.coverageKey),
    [rows, unattributed, report],
  );

  /**
   * "Total" whenever the API supplied window-wide sums — the figure no longer depends on
   * which page is on screen. Only the fallback path, summing visible rows, has to admit
   * it is a page.
   */
  /**
   * The report's guide content: `ecom_code`'s own "What this report shows"/"How ... are
   * calculated" copy for this report (see `reportGuides.tsx` — ported verbatim where the
   * two platforms' columns match, adapted where they have already diverged), plus this
   * platform's own live Coverage/Dates-and-money/scope-note context appended below it.
   *
   * Sub-points with nothing to say are DROPPED rather than rendered empty: a bullet that
   * says "Coverage:" and then nothing reads as a bug, and the coverage figure genuinely
   * does not exist until the first response arrives.
   */
  const guide = useMemo(() => reportGuideContent(report), [report]);

  const whatContent = useMemo(() => {
    const extras: { title: string; body: string }[] = [];

    if (tracked !== null) {
      extras.push({
        title: "Coverage",
        body:
          `This report can place ${tracked.tracked.toLocaleString("en-US")} of ` +
          `${tracked.total.toLocaleString("en-US")} orders (${String(tracked.percent)}%) in this range. ` +
          `The rest are shown in the "${unattributedLabel ?? "not matched"}" line — usually days before ` +
          `tracking was live, or clicks that carried no campaign.`,
      });
    }

    if (timezone !== null) {
      extras.push({
        title: "Dates and money",
        body: `Days are counted in the store's timezone (${timezone}); money is in the store's own currency.`,
      });
    }

    return (
      <div className="flex flex-col gap-3">
        {guide.whatContent}
        {extras.length > 0 && (
          <dl className="flex flex-col gap-2 text-[13px] leading-relaxed">
            {extras.map((extra) => (
              <div key={extra.title}>
                <dt className="text-foreground text-xs font-semibold tracking-wide uppercase">{extra.title}</dt>
                <dd className="mt-0.5">{extra.body}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    );
  }, [guide, tracked, timezone, unattributedLabel]);

  const howContent = (
    <div className="flex flex-col gap-3">
      {guide.howContent}
      <div>
        <p className="text-foreground m-0 text-xs font-semibold tracking-wide uppercase">How rows reconcile</p>
        <p className="mt-0.5 text-[13px] leading-relaxed">{report.scopeNote}</p>
      </div>
    </div>
  );

  const totalLabel =
    totals !== null || (offset === 0 && !canPageForward)
      ? search !== ""
        ? "Total of matches"
        : "Total"
      : "Page total";

  /**
   * The total, WHERE THE QUERY COMPUTES ONE. Read from the first row because the count is
   * a window function — the same value repeated on every row — and parsed defensively
   * because it arrives as a string like every other 64-bit count.
   */
  // The query's own row count is the REPORT's, not the matches'. Under a search the API sends how
  // many rows match instead (search_match_total, 2026-10-28), so the pager counts the matches.
  const total =
    search !== ""
      ? parseTotal(rows[0]?.["search_match_total"])
      : report.totalKey === undefined
        ? null
        : parseTotal(rows[0]?.[report.totalKey]);

  const showHierarchyControls =
    report.googleHierarchy === true || report.metaHierarchy === true;

  /**
   * Does a row click mean anything right now?
   *
   * On Meta: whenever the current level has a child — every level does except "ad".
   *
   * On Google: only for the campaign types whose `GOOGLE_HIERARCHY_CHILD` entry gives the
   * current level a real next level. Shopping and Multi-channel have no entry at all,
   * "all types" has no single campaignType to look up, and Unattributed/terminal levels
   * (PMax's "assetgroup", Search's "ad") resolve to `null` — all of those leave a row
   * unclickable.
   */
  const canDrillByClick =
    (report.metaHierarchy === true &&
      META_HIERARCHY_CHILD[hierarchy as MetaCampaignHierarchy] !== null) ||
    (report.googleHierarchy === true &&
      campaignType !== "all" &&
      (GOOGLE_HIERARCHY_CHILD[campaignType]?.[
        hierarchy as GoogleCampaignHierarchy
      ] ?? null) !== null);

  const handleDrillInto = canDrillByClick
    ? (row: ReportRow) => {
        const id = row["campaign_id"];
        const name = row["campaign_name"];
        if (id === null || id === undefined) return;
        const label =
          name === null || name === undefined ? String(id) : String(name);

        if (report.metaHierarchy === true) {
          const currentLevel = hierarchy as MetaCampaignHierarchy;
          const child = META_HIERARCHY_CHILD[currentLevel];
          if (child === null) return;
          setMetaDrillPath((path) => [
            ...path,
            { id: String(id), name: label, level: currentLevel },
          ]);
          setHierarchy(child);
          setOffset(0);
          return;
        }

        if (report.googleHierarchy === true && campaignType !== "all") {
          const currentLevel = hierarchy as GoogleCampaignHierarchy;
          const child = GOOGLE_HIERARCHY_CHILD[campaignType]?.[currentLevel];
          if (child === null || child === undefined) return;
          setGoogleDrillPath((path) => [
            ...path,
            { id: String(id), name: label, level: currentLevel },
          ]);
          setHierarchy(child);
          setOffset(0);
        }
      }
    : undefined;

  /** Jump to a point in the trail. `-1` means back out to the unscoped top-level view. */
  function goToMetaCrumb(index: number): void {
    const nextPath = index < 0 ? [] : metaDrillPath.slice(0, index + 1);
    setMetaDrillPath(nextPath);
    setHierarchy(
      nextPath.length === 0
        ? "campaign"
        : nextPath.length === 1
          ? "adset"
          : "ad",
    );
    setOffset(0);
  }

  /**
   * Jump to a point in the Google drill trail. `-1` backs out to the unscoped top-level
   * view. Unlike Meta's fixed campaign/adset/ad positions, the level after truncation is
   * not a fixed index — the chain's length and shape depend on `campaignType` (two steps
   * for Performance Max, three for Search/Demand Gen/Video) — so it is derived by walking
   * `GOOGLE_HIERARCHY_CHILD` from the crumb being landed on.
   */
  function goToGoogleCrumb(index: number): void {
    const nextPath = index < 0 ? [] : googleDrillPath.slice(0, index + 1);
    setGoogleDrillPath(nextPath);
    const last = nextPath[nextPath.length - 1];
    const level: GoogleCampaignHierarchy =
      last === undefined || campaignType === "all"
        ? "campaign"
        : (GOOGLE_HIERARCHY_CHILD[campaignType]?.[last.level] ?? "campaign");
    setHierarchy(level);
    setOffset(0);
  }

  return (
    <section className="flex min-w-0 flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2.5 text-2xl font-bold tracking-tight">
            <BarChart3 className="text-primary size-6" />
            {report.label}
            {/*
              Every explanation for this report lives behind this button — owner's
              decision, 2026-09-03, now shown as `ecom_code`'s report-guide affordance
              (auto-tooltip once, then an inline accordion) rather than a floating panel.
              Four paragraphs of prose used to stand between the title and the first number.
            */}
            <ReportGuideToggleButton
              showGuide={showGuideSection}
              onToggle={() => setShowGuideSection((current) => !current)}
            />
          </h1>
        </div>

        <div className="flex items-center gap-2">
          <ExportButton
            report={report}
            from={range.from}
            to={range.to}
            disabled={problem !== null}
            filterState={{
              sortBy,
              direction,
              status,
              campaignType,
              hierarchy,
              metaCampaignId,
              metaAdsetId,
              googleCampaignId,
              googleAdgroupId,
              search,
            }}
            visibleColumns={visibleColumns}
          />
          <DateRangePicker
            value={range}
            onApply={(next) => {
              setOffset(0);
              onRangeChange(next);
            }}
          />
        </div>
      </header>

      <ReportGuideAccordion
        showGuide={showGuideSection}
        whatContent={whatContent}
        {...(guide.howTitle === undefined ? {} : { howTitle: guide.howTitle })}
        {...(guide.howBadge === undefined ? {} : { howBadge: guide.howBadge })}
        howContent={howContent}
      />

      {problem !== null && (
        <p className="text-destructive text-sm" role="alert">
          {problem}
        </p>
      )}
      {error !== null && (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      )}

      {/*
        THE DRILL-DOWN TRAIL. Meta only — clicking a campaign row narrows the same table
        to that campaign's adsets, and clicking an adset narrows it to that adset's ads.
        Shown only once a click has happened; an empty trail at the top level would just
        be a lone "All campaigns" label with nothing to say.
      */}
      {report.metaHierarchy === true && metaDrillPath.length > 0 && (
        <nav
          aria-label="Drill-down"
          className="text-muted-foreground -mb-1 flex flex-wrap items-center gap-1 text-sm"
        >
          <button
            type="button"
            className="hover:text-foreground underline-offset-2 hover:underline"
            onClick={() => goToMetaCrumb(-1)}
          >
            All campaigns
          </button>
          {metaDrillPath.map((crumb, index) => (
            <span key={crumb.id} className="flex items-center gap-1">
              <ChevronRight className="size-3.5" aria-hidden="true" />
              {index === metaDrillPath.length - 1 ? (
                <span className="text-foreground font-medium">
                  {crumb.name}
                </span>
              ) : (
                <button
                  type="button"
                  className="hover:text-foreground underline-offset-2 hover:underline"
                  onClick={() => goToMetaCrumb(index)}
                >
                  {crumb.name}
                </button>
              )}
            </span>
          ))}
        </nav>
      )}

      {/*
        THE GOOGLE DRILL-DOWN TRAIL — same shape as Meta's above, populated only for the
        campaign types where a click means "zoom in" (see GOOGLE_HIERARCHY_CHILD):
        Performance Max, Search, Demand Gen, Video. Shopping's next level is a product
        catalog, Multi-channel's "asset group" is really ad-group grain under the hood,
        and neither gets a click-to-drill step, so this never renders for them.
      */}
      {report.googleHierarchy === true && googleDrillPath.length > 0 && (
        <nav
          aria-label="Drill-down"
          className="text-muted-foreground -mb-1 flex flex-wrap items-center gap-1 text-sm"
        >
          <button
            type="button"
            className="hover:text-foreground underline-offset-2 hover:underline"
            onClick={() => goToGoogleCrumb(-1)}
          >
            All campaigns
          </button>
          {googleDrillPath.map((crumb, index) => (
            <span key={crumb.id} className="flex items-center gap-1">
              <ChevronRight className="size-3.5" aria-hidden="true" />
              {index === googleDrillPath.length - 1 ? (
                <span className="text-foreground font-medium">
                  {crumb.name}
                </span>
              ) : (
                <button
                  type="button"
                  className="hover:text-foreground underline-offset-2 hover:underline"
                  onClick={() => goToGoogleCrumb(index)}
                >
                  {crumb.name}
                </button>
              )}
            </span>
          ))}
        </nav>
      )}

      {/* The Shopify-comparable figure (2026-10-20), beside the table rather than in it: the rows credit
          ORDERS on their order date; these cards count each cancellation and refund on its own day. */}
      {storeTotalSales !== null && !loading && !breakdownMode && <StoreTotalSalesCards value={storeTotalSales} />}

      {report.returnsView === true && (
        <ReturnsBridge totals={loading ? null : totals} view={returnsView} onViewChange={setReturnsView} />
      )}

      {!loading && <DataAsOfNote dataAsOf={dataAsOf} timezone={timezone} />}

      <div className="bg-card min-w-0 overflow-hidden rounded-xl border shadow-sm">
        <div className="flex flex-wrap items-center gap-3 border-b p-3">
          {/* A fixed, readable width: as `flex-1` it shrank to ~70 px beside the Type and
              Level controls (uat, 13 Oct) and the typed text could not be read; 320 px still cut
              a URL short (QA, 2026-10-28) — 384 px, 448 px on wide screens, the full text on hover. */}
          <div className="relative w-full shrink-0 sm:w-96 lg:w-[28rem]">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
              type="search"
              placeholder={`Search ${searchHint(report)}…`}
              aria-label="Search the report"
              className="h-9 pl-8"
              title={query}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                if (event.target.value.trim() === "") submitSearch("");
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") submitSearch(query);
              }}
            />
            {searchPending && query.trim() !== "" && (
              <p className="text-muted-foreground absolute top-full left-0 mt-0.5 text-[11px]">Press Enter to search</p>
            )}
          </div>

          <div className="flex-1" />

          {/* Google-only: the Type filter. Meta has no campaignType concept, so this
              never renders on the Meta report. */}
          {report.googleHierarchy === true && (
            <>
              <Label
                htmlFor="campaignType"
                className="text-muted-foreground text-xs"
              >
                Type
              </Label>
              <Select
                id="campaignType"
                className="h-9 w-[9.5rem]"
                value={campaignType}
                onChange={(event) => {
                  setOffset(0);
                  setCampaignType(
                    event.target.value as GoogleCampaignType | "all",
                  );
                  // A different type invalidates any trail built under the previous
                  // one — a Search ad group id means nothing under Performance Max —
                  // so the drill resets to the unscoped top-level view.
                  setGoogleDrillPath([]);
                  setHierarchy("campaign");
                }}
              >
                <option value="all">All types</option>
                {GOOGLE_CAMPAIGN_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {GOOGLE_CAMPAIGN_TYPE_LABELS[type]}
                  </option>
                ))}
              </Select>
            </>
          )}

          {/* The Level filter — shared between Google and Meta, driven by whichever
              hierarchy set applies to this report. Locked on Google when there is
              nothing meaningful to choose (see hierarchyLocked); never locked on Meta.
              On both platforms this stays in sync with the click trail above: picking a
              level here directly is still allowed and simply drops any now-stale parent
              scope the same way it always has. */}
          {showHierarchyControls && (
            <>
              <Label
                htmlFor="hierarchy"
                className="text-muted-foreground text-xs"
              >
                Level
              </Label>
              <Select
                id="hierarchy"
                className="h-9 w-[8.5rem]"
                value={hierarchy}
                disabled={hierarchyLocked}
                onChange={(event) => {
                  setOffset(0);
                  const next = event.target.value;
                  setHierarchy(next);
                  // Manually picking a level shorter than the current trail (e.g. jumping
                  // straight back to "campaign" from "ad") must drop the now-stale parent
                  // scope, or the next fetch would still be scoped to a campaign/adset the
                  // dropdown no longer shows.
                  if (report.metaHierarchy === true) {
                    if (next === "campaign") setMetaDrillPath([]);
                    else if (next === "adset")
                      setMetaDrillPath((path) => path.slice(0, 1));
                  }
                  // Same reasoning on Google: a manual Level pick is the unscoped view,
                  // so any click-built parent scope is dropped rather than left stale.
                  if (report.googleHierarchy === true) setGoogleDrillPath([]);
                }}
              >
                {(hierarchyLocked ? ["campaign"] : allowedHierarchies).map(
                  (level) => (
                    <option key={level} value={level}>
                      {hierarchyLabel(report, level)}
                    </option>
                  ),
                )}
              </Select>
            </>
          )}

          {/* Consolidated (the default) or the same rows by Day / Week — before the objective picker, as asked. */}
          {report.periodBreakdown !== undefined && (
            <>
              <Label htmlFor="period-view" className="text-muted-foreground text-xs">
                View
              </Label>
              <Select
                id="period-view"
                className="h-9 w-[8.5rem]"
                value={view}
                disabled={periodViewDisabled}
                title={periodViewDisabled ? "Not available for the Unattributed type" : undefined}
                onChange={(event) => {
                  setOffset(0);
                  setView(event.target.value as "consolidated" | "day" | "week");
                }}
              >
                <option value="consolidated">Consolidated</option>
                <option value="day">Day</option>
                <option value="week" disabled={weekDisabled}>
                  {weekDisabled ? "Week (needs > 7 days)" : "Week"}
                </option>
              </Select>
            </>
          )}

          {attributionToggle !== undefined && (
            <AttributionModelPicker
              toggle={attributionToggle}
              selectedObjective={selectedObjective}
              selectedModelKeys={activeModelKeys}
              onSelectObjective={(key) => {
                setOffset(0);
                setSelectedObjective(key);
              }}
              onToggleModel={(key) => {
                setOffset(0);
                setSelectedModels((previous) => {
                  const next = new Set(previous);
                  if (next.has(key)) {
                    // At least one model stays selected — an empty picker would leave
                    // only the always-on Platform/EA Matrix columns with no control on
                    // screen explaining why every model group vanished.
                    if (next.size > 1) next.delete(key);
                  } else {
                    next.add(key);
                  }
                  return next;
                });
              }}
              onResetModels={() => {
                setOffset(0);
                setSelectedModels(new Set(attributionToggle.defaultModelKeys));
              }}
            />
          )}

          {report.statusFilter === true && campaignType !== "UNATTRIBUTED" && (
            <>
              <Label htmlFor="status" className="text-muted-foreground text-xs">
                Status
              </Label>
              <Select
                id="status"
                className="h-9 w-[6.5rem]"
                value={status}
                onChange={(event) => {
                  // Back to page one: the filtered result has fewer rows, so keeping
                  // offset 200 would land on an empty page for no visible reason.
                  setOffset(0);
                  setStatus(event.target.value as CampaignStatus);
                }}
              >
                {CAMPAIGN_STATUSES.map((option) => (
                  <option key={option} value={option}>
                    {option === "all"
                      ? "All"
                      : option === "active"
                        ? "Active"
                        : "Inactive"}
                  </option>
                ))}
              </Select>
            </>
          )}

          {/* Only the first column is frozen by default (2026-10-21) — the full frozen block left
              64 px to scroll on a 1366 px laptop. Locking brings back Status + Platform. */}
          {!breakdownMode && visibleColumns.slice(1).some((column) => column.sticky === true) && (
            <Button
              type="button"
              variant={lockPlatformColumns ? "secondary" : "outline"}
              size="sm"
              aria-pressed={lockPlatformColumns}
              title="Keep Spend, Revenue, ROAS and the other Platform columns in view while you scroll"
              onClick={() => {
                const next = !lockPlatformColumns;
                setLockPlatformColumns(next);
                writeLockPlatformColumns(next);
              }}
            >
              {lockPlatformColumns ? <Lock /> : <LockOpen />}
              {lockPlatformColumns ? "Platform columns locked" : "Lock platform columns"}
            </Button>
          )}

          {!breakdownMode && (
            <>
          <Label htmlFor="rows" className="text-muted-foreground text-xs">
            Rows
          </Label>
          <Select
            id="rows"
            className="h-9 w-[5.5rem]"
            value={limit}
            onChange={(event) => {
              setOffset(0);
              setLimit(Number(event.target.value));
            }}
          >
            {/* Never above the API's cap of 1,000 — it REFUSES a larger limit rather
                than clamping it, so an option of 5,000 here would only ever 400. */}
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Select>
            </>
          )}
        </div>

        {breakdownMode ? (
          <AttributionBreakdownTable
            key={`${tenantId}|${report.key}|${view}|${range.from}|${range.to}|${JSON.stringify(reportFilters)}`}
            client={client}
            report={report}
            columns={visibleColumns}
            mode={view === "week" ? "week" : "day"}
            from={range.from}
            to={range.to}
            filters={reportFilters}
          />
        ) : (
          <ReportTable
            lockPlatformColumns={lockPlatformColumns}
            columns={visibleColumns}
            rows={visibleRows}
            loading={loading}
            totalLabel={totalLabel}
            storeName={storeName}
            {...(handleDrillInto !== undefined
              ? { onRowClick: handleDrillInto }
              : {})}
            {...(search !== ""
              ? ({ emptyReason: "no-matches", search } as const)
              : unattributed !== null &&
                  Number(unattributed["orders_in_window"] ?? -1) === 0
                ? ({ emptyReason: "no-orders" } as const)
                : {})}
            totals={totals}
            unattributed={unattributed}
            {...(unattributedLabel === undefined ? {} : { unattributedLabel })}
            {...(supportsUnmatchedReasons(report, hierarchy)
              ? {
                  onUnattributedClick: () => {
                    setReasonsOpen(true);
                  },
                }
              : {})}
            onJourneyOpen={openJourney}
            journeyRowEligible={journeyRowEligible}
            sortBy={sortBy}
            direction={direction}
            onSort={(key) => {
              setOffset(0);
              if (key === sortBy)
                setDirection(direction === "desc" ? "asc" : "desc");
              else {
                setSortBy(key);
                // A newly chosen metric starts descending: "biggest first" is what someone
                // clicking Spend or Sessions means every time.
                setDirection("desc");
              }
            }}
          />
        )}
      </div>

      {journeyTarget !== null && (
        <CustomerJourneyModal
          client={client}
          tenantId={tenantId}
          target={journeyTarget}
          range={range}
          onClose={closeJourney}
        />
      )}

      {reasonsOpen && (
        <UnmatchedReasonsDrawer
          platform={report.metaHierarchy === true ? "meta" : "google"}
          rows={reasons}
          loading={reasonsLoading}
          error={reasonsError}
          onClose={() => {
            setReasonsOpen(false);
          }}
        />
      )}

      {/*
        THE PAGER SAYS ONLY WHAT IT KNOWS. Four of the five reports return rows and a
        timezone and no count at all, so there is no honest "page 3 of 9" to print for
        them — a full page MIGHT have more after it, a short page definitely does not.
        Source Attribution does compute a total, and where one exists it is shown. Saying
        "Rows 1-50" is true either way; inventing a total would not be.
      */}
      {!breakdownMode && (offset > 0 || canPageForward) && (
        <nav aria-label="Pagination" className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={offset === 0 || loading}
            onClick={() => setOffset(Math.max(0, offset - limit))}
          >
            Previous
          </Button>
          <span className="text-muted-foreground text-xs">
            Rows {offset + 1}–{offset + rows.length}
            {total !== null && ` of ${total.toLocaleString("en-US")}${search !== "" ? ` ${total === 1 ? "match" : "matches"}` : ""}`}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={!canPageForward || loading}
            onClick={() => setOffset(offset + limit)}
          >
            Next
          </Button>
        </nav>
      )}
    </section>
  );
}
